/**
 * Navigation Configuration
 * Fuente unica de verdad para la navegacion del sistema.
 * Controla secciones del sidebar, items del AppBar, y permisos RBAC.
 */
import { PERMISSIONS } from "../context/PermissionsContext/PermissionsContext";
import { PLAYGROUND_ENABLED } from "../modules/playground/common/constants";
import { CODING_OPS_V2_ENABLED } from "../modules/coding-opportunities-v2/common/constants";

// BillingV2 (Etapa 2) — todos sus módulos están EN TEST (badge "Test", bajo
// revisión de información y confirmación) con fake data. NO deben verse en
// producción. El gate es el
// entorno, igual que PLAYGROUND_ENABLED y CODING_OPS_V2_ENABLED:
//   - `yarn start`  → NODE_ENV = "development" → BILLING_V2_ENABLED = true
//   - `yarn build`  → NODE_ENV = "production"  → BILLING_V2_ENABLED = false
// En el bundle de producción la sección entera ni siquiera se registra en el
// sidebar. Cuando un módulo pase a productivo, sacarlo de esta sección.
const BILLING_V2_ENABLED = process.env.NODE_ENV === "development";

// Pre-Visit Planning: módulo PRODUCTIVO en piloto. Vive en el grupo Visit
// Cycle de la sección Billing V2 (Stage 2), visible en dev y prod por igual —
// la sección se monta siempre; lo DEV-ONLY son los demás módulos del paquete.
// El acceso real está restringido por allowlist de empleados dentro del módulo
// (REACT_APP_PREVISIT_EMPLOYEES); en el sidebar es visible/clickeable para todos
// salvo los roles excluidos abajo.
const PREVISIT_NAV_ITEM = {
  id: "previsit-planning",
  path: "/previsit-planning",
  labelKey: "nav.previsitPlanning",
  labelFallback: "Pre-Visit Planning",
  icon: "EventAvailableIcon",
  iconColor: "#00838f",
  permissions: null,
  badge: "New",
  // Módulo clínico: fuera del sidebar para el rol de call center (cca).
  // Gate de frontend mientras el módulo no tenga moduleKey en el catálogo RBAC.
  hiddenForRoleKeys: ["cca"],
};

// Iconos referenciados por string para importar en componentes
export const SIDEBAR_SECTIONS = [
  {
    id: "clinical",
    labelKey: "nav.clinical",
    labelFallback: "Clinical",
    icon: "LocalHospitalIcon",
    permissions: [
      PERMISSIONS.CAREGAPS_READ,
      PERMISSIONS.CODING_READ,
      PERMISSIONS.AI_USE,
      PERMISSIONS.NOTES_READ,
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.REFERRALS_READ,
      PERMISSIONS.LAB_READ,
      PERMISSIONS.ASSIGNMENTS_WRITE,
    ],
    items: [
      {
        id: "care-gaps",
        path: "/care-gaps",
        labelKey: "nav.careGaps",
        labelFallback: "Care Gaps",
        icon: "BarChartIcon",
        iconColor: "#1976d2",
        permissions: [PERMISSIONS.CAREGAPS_READ],
      },
      // Care Gaps (legacy) — oculto del sidebar. El módulo nuevo en
      // /care-gaps es la versión canónica. El legacy sigue accesible solo
      // por URL directa (/care-gaps-legacy) si se necesita revisión puntual.
      {
        id: "coding-opportunities",
        path: "/coding-opportunities",
        labelKey: "nav.codingOpportunities",
        labelFallback: "Coding Opportunities",
        icon: "CodeIcon",
        iconColor: "#ff9800",
        permissions: [PERMISSIONS.CODING_READ],
      },
      // Coding Opportunities V2 — EN DESARROLLO. Solo aparece en el sidebar
      // cuando NODE_ENV === "development" (CODING_OPS_V2_ENABLED). En el build
      // de producción el ítem ni siquiera se registra.
      ...(CODING_OPS_V2_ENABLED
        ? [
            {
              id: "coding-opportunities-v2",
              path: "/coding-opportunities-v2",
              labelKey: "nav.codingOpportunitiesV2",
              labelFallback: "Coding Opportunities V2",
              icon: "InsightsIcon",
              iconColor: "#0F6E6E",
              permissions: [PERMISSIONS.CODING_READ],
              badge: "New",
            },
          ]
        : []),
      {
        id: "ai-clinical-notes",
        path: "/ClinicalNoteAi",
        labelKey: "nav.aiClinicalNotes",
        labelFallback: "AI Clinical Notes",
        icon: "FactCheckIcon",
        iconColor: "#1565c0",
        permissions: [PERMISSIONS.AI_USE],
      },
      // {
      //   id: "clinical-notes",
      //   path: "/ClinicalNotesList",
      //   labelKey: "nav.clinicalNotes",
      //   labelFallback: "Clinical Notes",
      //   icon: "DescriptionIcon",
      //   iconColor: "#43a047",
      //   permissions: [PERMISSIONS.NOTES_READ],
      // },
      {
        id: "medication-reconciliation",
        path: "/MedicationReconciliationPage",
        labelKey: "nav.medicationReconciliation",
        labelFallback: "Medication Reconciliation",
        icon: "MedicationIcon",
        iconColor: "#be07e2",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "specialist-referrals",
        path: "/specialist-referrals",
        labelKey: "nav.specialistReferrals",
        labelFallback: "Specialist Referrals",
        icon: "LocalHospitalIcon",
        iconColor: "#7986cb",
        permissions: [PERMISSIONS.REFERRALS_READ],
      },
      {
        id: "lab-phlebotomy",
        path: "/LabPhlebotomy",
        labelKey: "nav.labPhlebotomy",
        labelFallback: "Lab Phlebotomy",
        icon: "ScienceIcon",
        iconColor: "#4db6ac",
        permissions: [PERMISSIONS.LAB_READ],
      },
      {
        id: "assignments",
        path: "/assignments",
        labelKey: "nav.assignments",
        labelFallback: "Assignments",
        icon: "PersonIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.ASSIGNMENTS_WRITE],
      },
      // Patient Geography — módulo nuevo (api/geography). Sin permission gate
      // todavía: el backend aún no registró su moduleKey en el catálogo
      // (pendiente #4 de docs/modulos/GeoSistem/11_BACKEND_RESULTADO_Y_PENDIENTES.md).
      {
        id: "patient-geography",
        path: "/patient-geography",
        labelKey: "nav.patientGeography",
        labelFallback: "Patient Geography",
        icon: "TravelExploreIcon",
        iconColor: "#0F6E6E",
        permissions: [PERMISSIONS.PATIENTS_READ],
        badge: "New",
        // Módulo clínico: fuera del sidebar para el rol de call center (cca).
        // Gate de frontend mientras el backend no registre patient_geography.
        hiddenForRoleKeys: ["cca"],
      },
    ],
  },
  {
    id: "care-management",
    labelKey: "nav.careManagement",
    labelFallback: "Care Management",
    icon: "FavoriteBorderIcon",
    permissions: [
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.CAREPLANS_READ,
    ],
    items: [
      {
        id: "discharged",
        path: "/clinical-info/discharged",
        labelKey: "nav.discharged",
        labelFallback: "Discharged",
        icon: "ExitToAppIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "admitted",
        path: "/clinical-info/admitted",
        labelKey: "nav.admitted",
        labelFallback: "Admitted",
        icon: "LocalHospitalIcon",
        iconColor: "#42a5f5",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "emergency",
        path: "/clinical-info/emergency",
        labelKey: "nav.emergency",
        labelFallback: "Emergency",
        icon: "EmergencyIcon",
        iconColor: "#ff7043",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      // High Cost — módulo independiente (/high-cost). Reemplaza al viejo de
      // clinical-info (/clinical-info/high-cost ahora redirige aquí).
      {
        id: "high-cost",
        path: "/high-cost",
        labelKey: "nav.highCost",
        labelFallback: "High Cost",
        icon: "AttachMoneyIcon",
        iconColor: "#66bb6a",
        permissions: [PERMISSIONS.PATIENTS_READ],
        // Chip que indica que el módulo volvió (estaba en mantenimiento).
        badge: "New",
      },
      {
        id: "high-risk",
        path: "/clinical-info/high-risk",
        labelKey: "nav.highRisk",
        labelFallback: "High Risk",
        icon: "WarningIcon",
        iconColor: "#ffa726",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "no-recent-visits",
        path: "/no-recent-visits",
        labelKey: "nav.noRecentVisits",
        labelFallback: "No Recent Visits",
        icon: "VisibilityOffIcon",
        iconColor: "#e57373",
        permissions: [PERMISSIONS.PATIENTS_READ],
        badge: "New",
      },
      {
        id: "care-plans",
        path: "/care-plan",
        labelKey: "nav.carePlans",
        labelFallback: "Care Plans",
        icon: "AssignmentIcon",
        iconColor: "#26a69a",
        permissions: [PERMISSIONS.CAREPLANS_READ],
      },
      // Patient Profiles: oculto en producción (mismo criterio que los módulos
      // de Stage 2) mientras no esté en el catálogo RBAC del backend. En dev
      // sigue visible; en prod la ruta queda solo por URL directa.
      ...(process.env.NODE_ENV === "development"
        ? [
            {
              id: "patient-profiles",
              path: "/patient-profiles",
              labelKey: "nav.patientProfiles",
              labelFallback: "Patient Profiles",
              icon: "AssignmentIndIcon",
              iconColor: "#8e24aa",
              permissions: [PERMISSIONS.PATIENTS_WRITE],
            },
          ]
        : []),
    ],
  },
  // Contact Center — la sección se monta SIEMPRE (dev y prod). La visibilidad
  // por usuario la resuelve el sidebar con user.modules (RBAC) para los módulos
  // registrados en Administration → Access Control. Inactives queda activo y
  // visible a usuarios autenticados mientras backend crea su moduleKey.
  {
    id: "contact-center",
    labelKey: "nav.contactCenter",
    labelFallback: "Contact Center",
    icon: "SupportAgentIcon",
    permissions: null,
    items: [
      {
        id: "call-center-intake",
        path: "/call-center-intake",
        labelKey: "nav.callCenterIntake",
        labelFallback: "Call Center Intake",
        icon: "PhoneInTalkIcon",
        iconColor: "#5C6BC0",
        permissions: null,
        badge: "New",
      },
      {
        id: "transportation",
        path: "/transportation",
        labelKey: "nav.transportation",
        labelFallback: "Transportation",
        icon: "DirectionsBusIcon",
        iconColor: "#26a69a",
        permissions: null,
        badge: "New",
      },
      {
        id: "new-members",
        path: "/new-members",
        labelKey: "nav.newMembers",
        labelFallback: "New Members",
        icon: "PersonAddIcon",
        iconColor: "#ef6c00",
        permissions: null,
        badge: "New",
      },
      {
        id: "inactives",
        path: "/inactives",
        labelKey: "nav.inactives",
        labelFallback: "Inactives",
        icon: "PersonOffIcon",
        iconColor: "#d97706",
        permissions: null,
        badge: "New",
      },
      {
        id: "call-recent-visit",
        path: "/call-recent-visit",
        labelKey: "nav.callRecentVisit",
        labelFallback: "No Recent Visit",
        icon: "EventBusyIcon",
        iconColor: "#c2185b",
        permissions: null,
        badge: "New",
      },
      {
        id: "welcome-oep",
        path: "/welcome-oep",
        labelKey: "nav.welcomeOep",
        labelFallback: "Welcome & OEP",
        icon: "CampaignIcon",
        iconColor: "#7e57c2",
        permissions: null,
        badge: "New",
      },
    ],
  },
  // ===========================================================================
  // Etapa 2 — BillingV2 (docs/modulos/BillingV2). Módulos en test (badge "Test",
  // bajo revisión de información y confirmación): funcionan con fake data
  // alineada al contrato real hasta que el backend exponga sus endpoints.
  // Coding Opportunities (Módulo E) ya es productivo y vive en la sección
  // Clinical.
  //
  // La sección se monta SIEMPRE: contiene Pre-Visit Planning (productivo, en
  // el grupo Visit Cycle). El resto de módulos del paquete y la guía siguen
  // siendo DEV-ONLY (BILLING_V2_ENABLED) — en producción la sección solo
  // muestra lo productivo.
  // ===========================================================================
  {
    id: "billing-v2",
    labelKey: "nav.billingV2",
    labelFallback: "Billing V2 (Stage 2)",
    icon: "ReceiptLongIcon",
    permissions: null,
    // Items agrupados con subheaders ({ type: "subheader" }) replicando los
    // 5 grupos del Stage 2 Guide (src/modules/stage2-guide). El orden es el
    // orden recomendado de uso que documenta la guía.
    items: [
      ...(BILLING_V2_ENABLED
        ? [
            {
              id: "stage2-guide",
              path: "/stage2-guide",
              labelKey: "nav.stage2Guide",
              labelFallback: "Stage 2 Guide",
              icon: "MenuBookIcon",
              iconColor: "#0F6E6E",
              permissions: null,
            },
          ]
        : []),
      {
        type: "subheader",
        id: "bv2-group-visit-cycle",
        labelKey: "nav.bv2GroupVisitCycle",
        labelFallback: "Visit Cycle",
      },
      PREVISIT_NAV_ITEM,
      ...(BILLING_V2_ENABLED
        ? [
      {
        id: "cdci",
        path: "/cdci",
        labelKey: "nav.cdci",
        labelFallback: "CDCI — Provider Workspace",
        icon: "EventNoteIcon",
        iconColor: "#0F6E6E",
        permissions: null,
        badge: "New",
      },
      {
        id: "charge-capture",
        path: "/charge-capture",
        labelKey: "nav.chargeCapture",
        labelFallback: "Charge Capture",
        icon: "ReceiptLongIcon",
        iconColor: "#7e57c2",
        permissions: null,
        badge: "New",
      },
      {
        id: "denials-management",
        path: "/denials-management",
        labelKey: "nav.denialsManagement",
        labelFallback: "Denials Management",
        icon: "GavelIcon",
        iconColor: "#B5760A",
        permissions: null,
        badge: "New",
      },
      {
        type: "subheader",
        id: "bv2-group-population",
        labelKey: "nav.bv2GroupPopulation",
        labelFallback: "Population Care",
      },
      {
        id: "post-discharge",
        path: "/post-discharge",
        labelKey: "nav.postDischarge",
        labelFallback: "Post-Discharge",
        icon: "ExitToAppIcon",
        iconColor: "#1B5FA8",
        permissions: null,
        badge: "Test",
      },
      {
        id: "high-risk-surveillance",
        path: "/high-risk-surveillance",
        labelKey: "nav.highRiskSurveillance",
        labelFallback: "High Risk Surveillance",
        icon: "MonitorHeartIcon",
        iconColor: "#C0392B",
        permissions: null,
        badge: "Test",
      },
      {
        id: "medication-adherence",
        path: "/medication-adherence",
        labelKey: "nav.medicationAdherence",
        labelFallback: "Medication Adherence",
        icon: "MedicationIcon",
        iconColor: "#2e7d32",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-growth",
        labelKey: "nav.bv2GroupGrowth",
        labelFallback: "Membership & Outreach",
      },
      {
        id: "member-outreach",
        path: "/member-outreach",
        labelKey: "nav.memberOutreach",
        labelFallback: "Member Outreach",
        icon: "CampaignIcon",
        iconColor: "#ef6c00",
        permissions: null,
        badge: "Test",
      },
      {
        id: "member-onboarding",
        path: "/member-onboarding",
        labelKey: "nav.memberOnboarding",
        labelFallback: "New Member Onboarding",
        icon: "PersonAddIcon",
        iconColor: "#00695c",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-provider",
        labelKey: "nav.bv2GroupProvider",
        labelFallback: "Provider Improvement",
      },
      {
        id: "provider-doc-coach",
        path: "/provider-doc-coach",
        labelKey: "nav.providerDocCoach",
        labelFallback: "Provider Doc Coach",
        icon: "SchoolIcon",
        iconColor: "#6a1b9a",
        permissions: null,
        badge: "Test",
      },
      {
        id: "provider-performance",
        path: "/provider-performance",
        labelKey: "nav.providerPerformance",
        labelFallback: "Provider Performance",
        icon: "LeaderboardIcon",
        iconColor: "#5C6BC0",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-dashboards",
        labelKey: "nav.bv2GroupDashboards",
        labelFallback: "Executive Dashboards",
      },
      {
        id: "membership-analytics",
        path: "/membership-analytics",
        labelKey: "nav.membershipAnalytics",
        labelFallback: "Membership Analytics",
        icon: "GroupsIcon",
        iconColor: "#3949ab",
        permissions: null,
        badge: "Test",
      },
      {
        id: "clinical-operations",
        path: "/clinical-operations",
        labelKey: "nav.clinicalOperations",
        labelFallback: "Clinical Operations",
        icon: "TimelineIcon",
        iconColor: "#0277bd",
        permissions: null,
        badge: "Test",
      },
      {
        id: "executive-financial",
        path: "/executive-financial",
        labelKey: "nav.executiveFinancial",
        labelFallback: "Executive Financial",
        icon: "AttachMoneyIcon",
        iconColor: "#1F8A5B",
        permissions: null,
        badge: "Test",
      },
          ]
        : []),
    ],
  },
  {
    id: "general",
    labelKey: "nav.general",
    labelFallback: "General",
    icon: "CalendarMonthIcon",
    permissions: null, // Accesible para todos los autenticados
    items: [
      {
        id: "schedule",
        path: "/general",
        labelKey: "nav.schedule",
        labelFallback: "Schedule",
        icon: "CalendarMonthIcon",
        iconColor: "#667EEA",
        permissions: null,
      },
      {
        id: "tasks",
        path: "/tasks",
        labelKey: "nav.tasks",
        labelFallback: "Tasks",
        icon: "AssignmentIcon",
        iconColor: "#6366F1",
        permissions: null,
      },
      {
        id: "technical-issues",
        path: "/technical-issues",
        labelKey: "nav.technicalIssues",
        labelFallback: "Technical Issues",
        icon: "BugReportIcon",
        iconColor: "#EF4444",
        permissions: null,
      },
    ],
  },
  {
    id: "administration",
    labelKey: "nav.administration",
    labelFallback: "Administration",
    icon: "AdminPanelSettingsIcon",
    permissions: [PERMISSIONS.ADMIN_USERS, PERMISSIONS.SUPPORT_ACCESS, PERMISSIONS.DOCUMENTS_READ],
    items: [
      {
        id: "administration",
        path: "/administration",
        labelKey: "nav.administration",
        labelFallback: "Administration",
        icon: "AdminPanelSettingsIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.ADMIN_USERS],
      },
      {
        id: "library",
        path: "/library",
        labelKey: "nav.library",
        labelFallback: "Library",
        icon: "DescriptionIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.DOCUMENTS_READ],
      },
      {
        id: "support",
        path: "/support",
        labelKey: "nav.technicalSupport",
        labelFallback: "Technical Support",
        icon: "BugReportIcon",
        iconColor: "#9e9e9e",
        permissions: [PERMISSIONS.SUPPORT_ACCESS],
      },
    ],
  },
  // Playground (DEV-ONLY) — la sección entera se monta solo cuando
  // PLAYGROUND_ENABLED (NODE_ENV === "development"). En producción este spread
  // queda vacío y no aparece en el sidebar. El gate es el entorno, no el RBAC.
  ...(PLAYGROUND_ENABLED
    ? [
        {
          id: "dev",
          labelKey: "nav.devTools",
          labelFallback: "Dev Tools",
          icon: "TerminalIcon",
          permissions: null,
          items: [
            {
              id: "playground",
              path: "/playground",
              labelKey: "nav.playground",
              labelFallback: "Playground",
              icon: "TerminalIcon",
              iconColor: "#9c27b0",
              permissions: null,
            },
          ],
        },
      ]
    : []),
];

export const APPBAR_ITEMS = [
  {
    id: "dashboard",
    path: "/main",
    labelKey: "nav.dashboard",
    labelFallback: "Dashboard",
    icon: "HomeIcon",
    permissions: null, // Accesible para todos
  },
  {
    id: "patient-map",
    path: "/patientMap",
    labelKey: "nav.patientMap",
    labelFallback: "Patient Map",
    icon: "MapIcon",
    permissions: [PERMISSIONS.PATIENTS_READ],
  },
];

export default SIDEBAR_SECTIONS;
